import { runtimeConfig } from "config/runtimeConfig";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CHANNEL_ERROR,
  Constants,
  connectionHeaders,
  connectionTableApi,
  connectionTableHeaders,
  LOGS_AUTO_REFRESH_INTERVAL_MS,
} from "./Constants";
import { AppContext } from "../../../Context/AppContext";
/*
  Still imported, and deliberately.

  This module no longer USES most of what the file defines — the drawers are DS
  `SidePanel`s and the header is DS `Typography` — but the stylesheet also carries
  global class names that other, unmigrated screens depend on without importing it
  themselves: `.form-header` and `.connection-text` for `Pages/GMS/ARS/ARSLogs.tsx`,
  and `.form-group` for six `Components/GMS/Builder/*` files. ARSLogs imports it
  directly; the Builder files do not, so they are relying on some other module having
  pulled it into the bundle.

  Dropping the import here would therefore change how a Builder screen looks, from a
  connector change, with nothing in either file to suggest a link. That is exactly the
  action-at-a-distance § 109 documented for the duplicated `RulesLog.css`. The import
  stays until those consumers are migrated or given their own stylesheet.
*/
import "./Connection.css";

import DataImportForm from "./DataImportForm";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import ConnectionLogs from "./ConnectionLogs";
import NoData from "./NoData";
import { toast } from "Components/DesignSystem/Toast/useToast";
import useTable from "../../Common/Table/useTable";
import { useNavigate } from "react-router";
import ConfigurationPanel from "./ConfigurationPanel";
import PageSkeleton from "Components/DesignSystem/PageSkeleton/PageSkeleton";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";
import Tab from "Components/DesignSystem/Tab/Tab";
import Table from "Components/DesignSystem/Table/Table";
import Pagination from "Components/DesignSystem/Pagination/Pagination";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import { Refresh } from "Components/DesignSystem/Iconography/Icons";
import { toDsColumns } from "./connectionColumns";

// @todo replace
const MID_WAY_URL = runtimeConfig.CONNECTION_API;

/**
 * Page-size options.
 *
 * The values are the legacy set exactly, **including the fact that 100 is labelled
 * "All"**. It is not all — a channel with 150 ingestion runs shows 100 of them under
 * a label promising everything. That is a pre-existing defect and it is left alone
 * here on purpose: changing either the value or the label changes what a user sees on
 * a screen this commit is otherwise only re-skinning. Recorded in
 * `okf/connector/e2e.md` § 7 instead.
 */
const PAGE_SIZE_OPTIONS = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "All" },
];

const tabSlug = (tab) => tab.toLowerCase().replace(/\s+/g, "-");

const ConnectionsHome = () => {
  // app context
  const appData = useContext(AppContext);

  const _defaultModule = "Products";
  const [currentTab, setCurrentTab] = useState(null);

  const navigate = useNavigate();

  const [showImportDataForm, setShowImportDataForm] = useState(false);
  const [showConfigurationPanel, setShowConfigurationPanel] = useState(false);

  const [loading, setLoading] = useState(false);
  const [recordPerPage, setRecordPerPage] = useState(100);
  const [page, setPage] = useState(1);

  const [viewLog, setViewLog] = useState(false);
  const [logDataIndex, setLogDataIndex] = useState(undefined);
  const [logDataUid, setLogDataUid] = useState(undefined);
  const [canImportData, setCanImportData] = useState(false);
  const [loadingConfiguration, setLoadingConfiguration] = useState(false);
  const [gmsEndpoint, setGmsEndpoint] = useState(undefined);
  /* `null` when nothing is pending; otherwise `{ id, callback }` for the row awaiting
     confirmation. Replaces `BootBoxManager.confirm`, whose callback array was the only
     thing holding that state. */
  const [pendingDelete, setPendingDelete] = useState(null);

  const { headers, data, sort, setHeaders, setData, handleSortByColumn } =
    useTable({ table_headers: connectionHeaders });

  // Get the API path for a tab.
  //
  // "Related Products" used to be special-cased here onto logs/accessories whenever the connector's
  // gms_endpoint was below v3 -- including before configuration had loaded, since gmsEndpoint starts
  // undefined and parseFloat('') is NaN, so the comparison was false. That made an unconfigured
  // channel always read the wrong feed's logs.
  //
  // The gate is removed rather than corrected: it keyed off gms_endpoint, which is not the feed's
  // version at all. "relatedproducts" is the only key this tab sends today.
  //
  // The once-planned feed-version-driven mapping is dead: v1.0.1 was removed from the service's
  // relatedproducts FEED_VERSIONS on 2026-08-05 (approved retirement), so v2.0.0 /
  // "relatedproducts" is the only shape and no per-version routing is needed. If per-version
  // routing is ever revived, route on the version from feed_configurations -- never on
  // gms_endpoint again.
  const getApiPath = (tab) => connectionTableApi[tab];

  /**
   * handle tab change
   *
   * Re-selecting the ACTIVE tab refetches it — that is the only way to reload the
   * current feed apart from the Refresh button, and it is preserved.
   *
   * It used to do that from inside a `setCurrentTab` updater:
   *
   *     setCurrentTab((prevtab) => { if (prevtab === tab) fetchData(); return tab; });
   *
   * A state updater must be pure. React invokes it twice in StrictMode — which this
   * app runs in development — so re-clicking a tab fired two requests, and React is
   * free to replay an updater at any time. `currentTab` is already in scope and
   * already correct for this render, so the comparison needs no updater at all.
   *
   * @param {string} tab - tab label
   */
  const handleTabChange = (tab) => {
    if (currentTab === tab) {
      fetchData();
    } else {
      setCurrentTab(tab);
    }
    closeAllPanels();
  };

  const closeAllPanels = () => {
    setShowImportDataForm(false);
    setShowConfigurationPanel(false);
    setViewLog(false);
  };

  const fetchConfiguration = async () => {
    try {
      setLoadingConfiguration(true)
      const res = await ServiceWrapper.doGet(
        `${MID_WAY_URL}configuration/${appData?.channelKey}`,
        null,
        null,
        AuthManager.getGmsToken()
      );
      if (res?.data?.done) {
        setGmsEndpoint(res.data?.data?.gms_endpoint);
        setCanImportData(true);
      } else {
        setGmsEndpoint(undefined);
        setCanImportData(false);
      }
    }
    finally {
      setLoadingConfiguration(false);
      // Set the default tab whether or not configuration loaded — an unconfigured
      // channel still lists logs. `setCurrentTab` is a no-op when it already holds
      // this value, so the guard the two branches used to carry is unnecessary.
      setCurrentTab((prev) => prev ?? _defaultModule);
    }
  };

  const fetchData = async ({ silent = false } = {}) => {
    if (!currentTab) return;
    if (!silent) setLoading(true);
    const apiPath = getApiPath(currentTab);

    let res = await ServiceWrapper.doGet(
      `${MID_WAY_URL}connection/${appData?.channelKey}/${apiPath}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.errorfound || res?.data?.done === false) {
      if (!silent) toast.error(`Error loading ${currentTab.toLowerCase()} list`);
      setLoading(false);
      return;
    }

    setData(res.data.data);
    setLoading(false);
  };

  /*
    Permission gate, on its own effect.

    Bundled with the fetches it re-ran on every tab change, which is harmless for a
    `navigate` but obscured that the two have nothing to do with each other.
  */
  useEffect(() => {
    if (!appData.pm.checkPermission("gmsconnector_read")) {
      navigate("/workspace/module/permission/denied");
    }
  }, [appData.pm, navigate]);

  /*
    Configuration is per CHANNEL, so it is fetched per channel.

    It used to sit in the same effect as `fetchData`, keyed on `[currentTab,
    channelKey]` — so `configuration/{channelKey}` was re-requested on **every tab
    click**. Six clicks around the tab strip meant six redundant round trips to
    `midway_connector_svc`, which `okf/connector/e2e.md` § 8 already flags as a remote
    host slower than the local backend. Nothing in the response varies by tab.
  */
  useEffect(() => {
    if (!appData.pm.checkPermission("gmsconnector_read")) return;
    if (appData?.channelKey) {
      fetchConfiguration();
    } else {
      console.error(CHANNEL_ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData?.channelKey]);

  /* The rows, which DO change per tab. */
  useEffect(() => {
    if (!appData.pm.checkPermission("gmsconnector_read")) return;
    // Guarded on `currentTab`: the first render has none, and
    // `connectionTableHeaders[null]` is `undefined`, which used to be written straight
    // into `headers` and left the table one truthy `data` away from `undefined.map`.
    if (currentTab) setHeaders(connectionTableHeaders[currentTab]);
    if (appData?.channelKey) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, appData?.channelKey]);

  useEffect(() => {
    if (!currentTab || currentTab === "Schedule" || !appData?.channelKey) return;
    if (!appData.pm.checkPermission("gmsconnector_read")) return;

    const intervalId = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchData({ silent: true });
    }, LOGS_AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [currentTab, appData?.channelKey]);

  /*
    Clamp the page when the list shrinks under it.

    This is the legacy `Paginator.redraw()`, which ran on every change to
    `totalRecords` or `recordPerPage` and pulled the current page back to the last one
    that exists. It is reproduced rather than replaced with `setPage(1)`, because
    resetting to the first page on every refresh is NOT what this screen did: hitting
    Refresh on page 3 of a stable list left you on page 3.

    Without it, a refresh that returns fewer rows leaves `page` pointing past the end,
    `pagedData` empty, and the table showing its empty state while the pagination
    underneath still reports rows.
  */
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil((data?.length ?? 0) / recordPerPage));
    if (page > lastPage) setPage(lastPage);
  }, [data, recordPerPage, page]);

  const handleViewLog = (index, row) => {
    setViewLog(true);
    setLogDataIndex(index);
    /* From the ROW, not `data[index]` — `columns` is memoised on `[headers]`, so this
       function is the first render's, and its `data` is the empty initial array. Read
       through it and the uid is always undefined, which silently drops the row back to
       index-identity and undoes exactly the fix this uid exists for. */
    setLogDataUid(row?.uid ?? data[index]?.uid);
  };

  const handleRecordPerPageChange = (perPage) => {
    setRecordPerPage(Number(perPage));
    setPage(1);
  };

  /**
   * Ask before deleting a schedule.
   *
   * `BootBoxManager.confirm(title, message, false, [handler])` — a jQuery-era global
   * that renders outside React, is invisible to jsdom (so this path had no unit
   * coverage at all), and takes its confirm handler as element 0 of an array. The DS
   * `Dialog` below is the same question with the same words.
   *
   * The legacy `options?.index ? handleDelete(index) : …` branch is not carried over:
   * no header set in `Constants.js` sets `index`, so it was unreachable from this
   * screen, and reproducing a dead branch would only preserve the confusion.
   */
  const requestDelete = (row, header) => {
    const apiKey = header?.options?.apiKey;
    setPendingDelete({
      id: apiKey ? row[apiKey] : undefined,
      callback: header?.options?.callback,
    });
  };

  const activeLogRow = logDataUid
    ? data?.find((row) => row?.uid === logDataUid)
    : data?.[logDataIndex];

  const confirmDelete = async () => {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (pending?.callback) await pending.callback(pending.id, fetchData);
  };

  const columns = useMemo(
    () =>
      toDsColumns(headers, {
        onViewLog: handleViewLog,
        onDelete: requestDelete,
        baseId: "connectors-table",
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers]
  );

  /* The legacy table sliced internally with this exact expression. It is here now
     because DS `Table` renders what it is given and does not paginate. */
  const pagedData = useMemo(
    () => (data ?? []).slice(page * recordPerPage - recordPerPage, page * recordPerPage),
    [data, page, recordPerPage]
  );

  const canSchedule = appData.pm.checkPermission("gmsconnector_schedule");

  const tabItems = useMemo(
    () =>
      Constants.Tabs.map((tb) => ({
        id: tb,
        label: tb,
        /* Schedule is a different data set from the six feed-log tabs — its own
           endpoint and its own columns — and the screen has always shown that as a
           gap. `align` is how that is said now; it used to be two separate divs of
           buttons inside a `justify-between` row, which meant NO tablist semantics:
           no roles, no arrow-key movement, no single tab stop. */
        align: tb === "Schedule" ? "end" : "start",
        disabled: tb === "Schedule" && !canSchedule,
        /*
          Name-based, replacing `connections-connectionshome-button-1-${index}`.
          `okf/connector/e2e.md` § 8 has flagged the index form as a flake source since
          this doc existed: inserting a feed tab silently repoints every spec after it,
          and nothing fails until a spec asserts on the wrong feed's logs.
        */
        "data-testid": `connectors-tab-${tabSlug(tb)}`,
      })),
    [canSchedule]
  );

  if (loadingConfiguration && !gmsEndpoint) return <PageSkeleton preset="list" label="Loading connections" data-testid="connectors-loading" />
  return (
    <>
      <div className="tw-flex tw-justify-between tw-items-center" data-testid="connectors-header">
        {/*
          `heading-2`, not `title-header`.

          The legacy `.title` class in `Connection.css` was **18px/600**. The first cut
          of this migration used `title-header`, which is **14px/600** — so the page
          heading quietly shrank by 4px and stopped out-ranking the tab labels beneath
          it. `heading-2` is 18px/500 and is what every other page title in this app
          renders at (`ISRHomePage`, `ListView`, `ListEdit`, `ListDetails`,
          `RuleView`), all of them `as="h1"`. The weight moves 600 → 500 because that
          is the token's own definition and what those five already look like; the
          size, which is what carries the hierarchy, is restored.
        */}
        <Typography className="tw-text-ink" variant="display" as="h1" data-testid="connectors-title">
          Manage Connections
        </Typography>

        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            variant="tertiary"
            size="sm"
            data-testid="connectors-refresh-btn"
            aria-label="Refresh connections"
            onClick={() => {
              closeAllPanels();
              fetchData();
            }}
          >
            <Refresh />
          </Button>

          <Button
            variant="secondary"
            data-testid="connectors-configuration-btn"
            onClick={() => setShowConfigurationPanel(true)}
          >
            Configuration
          </Button>

          <Button
            variant="primary"
            data-testid="connector-data-import-btn"
            disabled={!canImportData || !appData.pm.checkPermission("gmsconnector_trigger")}
            onClick={() => setShowImportDataForm(true)}
          >
            Data Import
          </Button>
        </div>
      </div>

      {currentTab && (
        <>
          <Tab
            tabs={tabItems}
            activeTabId={currentTab}
            onChange={handleTabChange}
            data-testid="connectors-tabs"
            className="tw-mt-2"
          />

          <div className="tw-mt-3">
            <Table
              columns={columns}
              data={pagedData}
              rowKey="uid"
              striped
              bordered
              stickyHeader
              maxHeight="75vh"
              loading={loading}
              sortState={sort?.column ? { columnId: sort.column, direction: sort.direction } : undefined}
              /*
                `handleSortByColumn` owns the cycle, not DS `Table`. The two disagree
                on the third click — `Table` offers asc → desc → unsorted, `useTable`
                cycles asc → desc → asc — and `useTable` is the shipped behaviour, so
                the direction `Table` computes is ignored and its arrow is driven from
                `sortState` above. The list therefore never returns to unsorted, which
                is what it did before.
              */
              onSort={(columnId) => handleSortByColumn(columnId)}
              data-testid="connectors-table"
              emptyContent={
                <NoData
                  module={currentTab}
                  canImportData={canImportData}
                  setShowImportDataForm={setShowImportDataForm}
                />
              }
            />

            {data && data.length > 0 && (
              <div className="tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-4">
                {/*
                  "Show" is a word in a sentence here — "Show [10]" — not a field label,
                  and the difference is visible: `Dropdown`'s `label` renders a FieldTitle
                  ABOVE the trigger, so passing it here stacked the word over the control
                  and left it floating above the row while "Showing 1-5 of 5" sat beside
                  the trigger. The rest of the app states this as an inline `Typography`
                  next to a label-less Dropdown — ISR, A/B Testing, Party/Channel/Domain
                  management, Lists, Rules and Catalog Management all do it this way. This
                  was the only one that did not.

                  The control keeps its accessible name through `aria-label`, which is what
                  every one of those sites does too.
                */}
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                    Show
                  </Typography>
                  <Dropdown
                    label=""
                    aria-label="Rows per page"
                    size="sm"
                    className="tw-w-[92px]"
                    options={PAGE_SIZE_OPTIONS}
                    value={recordPerPage}
                    onChange={handleRecordPerPageChange}
                    /* Portalled: the table above is a bounded, scrolling box, which
                       would clip a menu opening upward near the foot of the page. */
                    usePortal
                    data-testid="connectors-page-size"
                  />
                </div>
                <Pagination
                  page={page}
                  pageSize={Number(recordPerPage)}
                  totalItems={data?.length ?? 0}
                  onPageChange={setPage}
                  size="sm"
                  data-testid="connectors-pagination"
                />
              </div>
            )}
          </div>
        </>
      )}

      {/*
        The three drawers stay CONDITIONALLY MOUNTED.

        Each owns a DS `SidePanel` internally, which can animate itself out — but only
        while it is still mounted, and unmounting on close is what guarantees a fresh
        form every time Data Import is reopened, and what makes `ConfigurationPanel`'s
        `setEditInProgress(false)` cleanup run. Keeping them mounted to buy an exit
        animation would trade a real invariant for a cosmetic one.

        Nothing is lost either way: the `CSSTransition` wrappers these replace never
        animated. `CSSTransition` defaults to `in={false}` and none of the three ever
        passed `in`, so the `animate__flipInX` / `flipOutX` classes were never applied
        in any state — three dead wrappers, each with a `key` and a 1000ms timeout, on
        an animation that has never run.
      */}
      {/* `activeLogRow`, not `data[logDataIndex]` (release-sept-29). The index is only
          a fallback now: this screen refreshes itself every 30s, and a refresh that
          reorders or shortens the list leaves a remembered INDEX pointing at a
          different connector's logs. The uid points at the row it was opened on. */}
      {viewLog && activeLogRow && (
        <ConnectionLogs
          logData={activeLogRow.logs}
          logId={activeLogRow.uid}
          setViewLog={setViewLog}
        />
      )}

      {showImportDataForm && (
        /* gmsEndpoint is no longer passed: the form's data types are the same for every
           connector version now that "accessories" is gone from the service's FEED_TYPES. */
        <DataImportForm
          setShowImportDataForm={setShowImportDataForm}
          setCurrentTab={handleTabChange}
          currentTab={currentTab}
          fetchData={fetchData}
        />
      )}

      {showConfigurationPanel && (
        <ConfigurationPanel
          setShowConfigurationPanel={setShowConfigurationPanel}
          callback={fetchConfiguration}
        />
      )}

      <Dialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        tone="danger"
        title="Delete Schedule?"
        data-testid="connectors-delete-schedule-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              data-testid="connectors-delete-schedule-cancel-btn"
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              data-testid="connectors-delete-schedule-confirm-btn"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" as="p">
          Do you really want to delete the Schedule ? This cannot be undone.
        </Typography>
      </Dialog>
    </>
  );
};

export default ConnectionsHome;
